import { create } from 'zustand';

import { inferCategory } from './lib/auto-category';
import { DEBUG } from './lib/debug';
import { detectInitialLang, type UiLangCode } from './lib/i18n';
import {
  clearAllSessions,
  deleteSession as storageDeleteSession,
  saveSession as storageSaveSession,
} from './lib/storage';
import { LANG_OPTIONS } from './lib/types';
import type {
  CorrectedImage,
  DebugEntry,
  ManualQuad,
  OcrResult,
  ProcessingStage,
  Receipt,
  ReceiptFinding,
  ReceiptSession,
} from './lib/types';

/**
 * Multi-receipt store.
 *
 * Each drop creates a `ReceiptSession` keyed by id. Sessions move through
 * states: queued → processing → success | error. The hook drives transitions
 * via setStage / setResult / setError; the UI reads sessions[] and renders
 * a list. updateReceipt is for inline edits after parsing succeeds.
 */
interface AppState {
  sessions: ReceiptSession[];

  /**
   * Active INTERFACE language (ISO 639-2 / Tesseract code). Drives the
   * UI translations in `lib/i18n.ts` and nothing else — the language a
   * receipt is OCR'd in is `ocrLang` / the per-session `lang`. Keeping
   * these apart matters because `setLang` also rewrites the URL to the
   * `/es/`-style prefix for SEO; a user picking the language their
   * *receipt* is in must not be navigated to a different localised page.
   * Initialised from the URL prefix, else localStorage, else
   * navigator.language. `setLang` writes through to localStorage so the
   * choice survives reloads.
   */
  lang: UiLangCode;
  setLang: (lang: UiLangCode) => void;

  /**
   * Default OCR language for newly-uploaded receipts — the Tesseract
   * model to load, independent of `lang`. `null` means the user hasn't
   * been asked yet: the first upload opens ReceiptLanguageDialog, which
   * offers "same as the interface" as a one-click confirm or a picker
   * for anything else. Once set it is never inferred again. Persisted
   * under its own localStorage key and, unlike `setLang`, never touches
   * the URL.
   */
  ocrLang: UiLangCode | null;
  setOcrLang: (lang: UiLangCode) => void;

  /**
   * User-controlled toggle for the debug overlays — DebugPanel (corrected
   * image + worker debug log when DEBUG builds) and OcrDebugPanel (raw
   * OCR text and per-line confidences). Off by default so the receipt
   * cards stay clean for normal use; flip on from the settings card to
   * peek under the hood. Persisted to localStorage so a power user
   * doesn't have to re-enable on every reload.
   */
  showDebug: boolean;
  setShowDebug: (v: boolean) => void;

  /**
   * Opt-in batch persistence. When true, terminal-state sessions and
   * later edits get written through to IndexedDB; on next visit the
   * batch is rehydrated. When toggled OFF, IndexedDB is cleared
   * immediately so a user changing their mind isn't surprised by data
   * lingering. Default OFF — sticky receipts across reloads should be
   * a deliberate choice given how sensitive receipt content is.
   */
  persistEnabled: boolean;
  setPersistEnabled: (v: boolean) => void;

  /**
   * When true, CSV / XLSX / ZIP exports omit the `Tax` AND `Subtotal`
   * columns/rows. Tax info is detrimental to export in some
   * jurisdictions (e.g., where individuals wouldn't normally hold
   * VAT-itemised data); the toggle lets users hand off the receipts
   * to bookkeepers or expense-report tools without leaking that.
   * Subtotal is bundled in because keeping it would expose the tax
   * indirectly via total − subtotal. Tip is independent and stays.
   * Default OFF — most users want full data in exports.
   */
  excludeTaxFromExports: boolean;
  setExcludeTaxFromExports: (v: boolean) => void;

  /**
   * Replace the in-memory sessions list with the supplied array.
   * Used by the boot path in App.tsx to rehydrate from IndexedDB.
   * Doesn't trigger any further persistence — callers pass in what
   * IDB returned, no need to write it back.
   */
  hydrate: (sessions: ReceiptSession[]) => void;

  /**
   * Add a new session in the queued state. Holds onto the original File
   * so the manual-crop modal can re-render the un-warped image without
   * re-prompting the user to upload it again.
   */
  enqueue: (
    id: string,
    filename: string,
    file: File,
    lang: string,
    singleUpload: boolean,
  ) => void;
  /**
   * Change the OCR language recorded on one session. Called just before
   * a language-triggered reprocess so the card shows the new language
   * while the re-run is in flight.
   */
  setSessionLang: (id: string, lang: string) => void;
  setStage: (
    id: string,
    stage: ProcessingStage,
    progress?: number,
    message?: string,
  ) => void;
  setResult: (
    id: string,
    receipt: Receipt,
    ocr: OcrResult,
    findings: ReceiptFinding[],
  ) => void;
  setError: (id: string, error: string, stage?: ProcessingStage) => void;
  /**
   * Replace the session's `originalFile` reference. Used by the manual-
   * rotate flow which client-side rotates the corrected JPEG and feeds
   * it back through the pipeline as a new dispatch — the rotated File
   * becomes the new "source of truth" for further reprocessing
   * (recrop / further rotates) so subsequent rotations compound
   * correctly and the recrop modal shows the rotated image.
   */
  setOriginalFile: (id: string, file: File) => void;
  updateReceipt: (id: string, receipt: Receipt) => void;
  appendDebug: (id: string, entry: DebugEntry) => void;
  /**
   * Store the worker's corrected image; when the message carried the crop
   * quad that produced it, record that too. quad omitted ≠ "no quad" —
   * skipCorrect re-posts carry none, and the previously detected quad
   * (still valid for the unchanged originalFile) must survive them.
   */
  setCorrectedImage: (id: string, image: CorrectedImage, quad?: ManualQuad) => void;
  /**
   * Park a session in the crop-review gate (see SessionState 'review').
   * Set when a correctOnly worker run finishes; the session leaves this
   * state via confirm (→ re-dispatch), re-crop, or removal.
   */
  setAwaitingReview: (id: string) => void;
  /**
   * Reset a session back to queued so it can be reprocessed in place.
   * Clears receipt / ocr / corrected image / findings / debug log but
   * keeps id, filename, and originalFile. Used by the manual-crop
   * fallback before re-dispatching the file with a manualQuad.
   */
  resetForReprocess: (id: string) => void;
  /**
   * Move a session from one index to another. Re-indexes `position` on
   * every session in the new order so subsequent loads sort correctly.
   * Persists every terminal-state session to IDB if persistence is on
   * (cheap given typical batch sizes — a 20-item batch reorder writes
   * ≤20 records).
   */
  reorderSessions: (fromIdx: number, toIdx: number) => void;
  removeSession: (id: string) => void;
  /** Remove every session in a terminal state (success | error). Leaves
   *  in-flight queued/processing entries alone. */
  removeTerminal: () => void;
  reset: () => void;
}

function patchSession(
  sessions: ReceiptSession[],
  id: string,
  patch: (s: ReceiptSession) => ReceiptSession,
): ReceiptSession[] {
  let changed = false;
  const next = sessions.map((s) => {
    if (s.id !== id) return s;
    changed = true;
    return patch(s);
  });
  return changed ? next : sessions;
}

// Two-letter URL prefix → 3-letter UiLangCode. Mirrored in
// scripts/gen-language-pages.mjs which writes the per-language
// dist/{xx}/index.html shells under the same path segments. The
// /es/ ↔ spa kind of mapping is the standard Tesseract UI-code
// convention (3-letter ISO 639-3 internally, 2-letter ISO 639-1
// in URLs / hreflang).
const URL_PREFIX_TO_LANG: Record<string, UiLangCode> = {
  es: 'spa',
  fr: 'fra',
  de: 'deu',
  nl: 'nld',
  it: 'ita',
  pt: 'por',
};

const LANG_TO_URL_PREFIX: Record<UiLangCode, string> = {
  eng: '',
  spa: 'es',
  fra: 'fr',
  deu: 'de',
  nld: 'nl',
  ita: 'it',
  por: 'pt',
};

/**
 * Inspect window.location.pathname for a `/{xx}/...` prefix mapping
 * to a UiLangCode. Returns null when on the root path.
 */
function langFromUrl(): UiLangCode | null {
  if (typeof window === 'undefined') return null;
  const m = /^\/([a-z]{2})(?:\/|$)/.exec(window.location.pathname);
  if (m === null) return null;
  return URL_PREFIX_TO_LANG[m[1]!] ?? null;
}

/**
 * Read a saved UiLangCode preference. Returns null when unset, invalid,
 * or localStorage is unavailable (private mode, locked-down origin).
 */
function loadLangPref(key: string): UiLangCode | null {
  try {
    const stored = localStorage.getItem(key);
    if (stored !== null && LANG_OPTIONS.some((opt) => opt.code === stored)) {
      return stored as UiLangCode;
    }
  } catch {
    // fall through to the caller's fallback
  }
  return null;
}

// Read initial lang in priority order: URL path prefix → localStorage
// → browser-language detection. The URL prefix takes precedence so a
// user landing on /es/ from a Spanish search result gets Spanish even
// if their last visit was English. localStorage is still updated on
// every setLang so subsequent visits to / preserve their choice.
function loadInitialLang(): UiLangCode {
  return langFromUrl() ?? loadLangPref('rr-lang') ?? detectInitialLang();
}

/**
 * Read a boolean preference. False when unset or localStorage is
 * unavailable — every boolean pref in this store defaults to off.
 */
function loadBoolPref(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

/**
 * Best-effort localStorage write. Failure is ignored — every read path
 * has its own fallback when storage is unavailable.
 */
function savePref(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

export const useAppStore = create<AppState>((set) => ({
  sessions: [],

  lang: loadInitialLang(),

  setLang: (lang) => {
    savePref('rr-lang', lang);
    // Navigate to the matching URL prefix so the address bar reflects
    // the chosen language. Uses pushState so the back button restores
    // the previous language. Skip when the URL is already correct (e.g.
    // user landed on /es/ and the picker mirror-set the same lang).
    if (typeof window !== 'undefined') {
      const prefix = LANG_TO_URL_PREFIX[lang];
      const targetPath = prefix === '' ? '/' : `/${prefix}/`;
      if (window.location.pathname !== targetPath) {
        try {
          window.history.pushState(null, '', targetPath);
        } catch {
          // History API unavailable (sandboxed iframe, etc.) — silently
          // skip; the language still applies in-memory.
        }
      }
    }
    set({ lang });
  },

  // Null = never chosen → App.tsx asks on first upload. Deliberately no
  // URL-prefix / navigator-language fallback: guessing is what this
  // replaces, and a wrong guess costs a full re-OCR plus a ~12 MB model
  // download.
  ocrLang: loadLangPref('rr-ocr-lang'),

  // No URL rewrite here, deliberately — see the `lang` / `ocrLang` docs
  // on AppState. Picking the language a receipt is written in must not
  // navigate the user to a differently-localised page.
  setOcrLang: (ocrLang) => {
    savePref('rr-ocr-lang', ocrLang);
    set({ ocrLang });
  },

  showDebug: loadBoolPref('rr-show-debug'),

  setShowDebug: (v) => {
    savePref('rr-show-debug', String(v));
    set({ showDebug: v });
  },

  persistEnabled: loadBoolPref('rr-persist'),

  setPersistEnabled: (v) => {
    savePref('rr-persist', String(v));
    set({ persistEnabled: v });
    if (v) {
      // Just turned ON: write the current sessions to IDB so a refresh
      // right now would still find them.
      persistAllTerminal();
    } else {
      // Just turned OFF: clear IDB so the next visit doesn't surprise
      // the user with stale data they thought they'd opted out of.
      void clearAllSessions();
    }
  },

  excludeTaxFromExports: loadBoolPref('rr-exclude-tax'),

  setExcludeTaxFromExports: (v) => {
    savePref('rr-exclude-tax', String(v));
    set({ excludeTaxFromExports: v });
  },

  hydrate: (sessions) => set({ sessions }),

  enqueue: (id, filename, file, lang, singleUpload) =>
    set((state) => ({
      sessions: [
        ...state.sessions,
        {
          id,
          filename,
          // max(position) + 1, not sessions.length: removeSession
          // doesn't re-index survivors, so after a removal the length
          // can collide with a survivor's position and make the order
          // nondeterministic after an IDB reload.
          position: state.sessions.reduce((max, s) => Math.max(max, s.position + 1), 0),
          state: { kind: 'queued' },
          receipt: null,
          ocrDebug: null,
          debugLog: [],
          correctedImage: null,
          findings: [],
          originalFile: file,
          lang,
          singleUpload,
        },
      ],
    })),

  setStage: (id, stage, progress, message) =>
    set((state) => ({
      sessions: patchSession(state.sessions, id, (s) => {
        const next: Extract<ReceiptSession['state'], { kind: 'processing' }> = {
          kind: 'processing',
          stage,
        };
        if (progress !== undefined) next.progress = progress;
        if (message !== undefined) next.message = message;
        return { ...s, state: next };
      }),
    })),

  setResult: (id, receipt, ocr, findings) => {
    // Auto-categorise via the layered heuristic: user memory first
    // (1.0 score), then vendor lookup table, then item-keyword scoring.
    // Skips when the receipt already has a category (re-process /
    // restored from IDB) so we don't overwrite a pinned choice.
    let enriched: Receipt = receipt;
    if (receipt.category === undefined) {
      const inference = inferCategory(receipt);
      if (inference !== null) {
        enriched = {
          ...receipt,
          category: inference.category,
          // User-memory matches are by definition authoritative — don't
          // tag them as "auto" in the UI; treat them as user-set so no
          // score badge shows up.
          ...(inference.source === 'memory'
            ? {}
            : { categoryScore: inference.score }),
        };
      }
    }
    set((state) => ({
      sessions: patchSession(state.sessions, id, (s) => ({
        ...s,
        state: { kind: 'success' },
        receipt: enriched,
        ocrDebug: ocr,
        findings,
      })),
    }));
    persistIfEnabled(id);
  },

  setError: (id, error, stage) => {
    set((state) => ({
      sessions: patchSession(state.sessions, id, (s) => ({
        ...s,
        state: stage ? { kind: 'error', error, stage } : { kind: 'error', error },
      })),
    }));
    persistIfEnabled(id);
  },

  setOriginalFile: (id, file) => {
    set((state) => ({
      sessions: patchSession(state.sessions, id, (s) => ({
        ...s,
        originalFile: file,
        // The old quad's coords were relative to the replaced original
        // (rotate swaps in the rotated corrected JPEG) — stale coords
        // would seed the crop modal with garbage.
        detectedQuad: undefined,
      })),
    }));
    persistIfEnabled(id);
  },

  updateReceipt: (id, receipt) => {
    set((state) => ({
      sessions: patchSession(state.sessions, id, (s) => ({ ...s, receipt })),
    }));
    persistIfEnabled(id);
  },

  appendDebug: (id, entry) =>
    set((state) => ({
      sessions: patchSession(state.sessions, id, (s) => ({
        ...s,
        debugLog: [...s.debugLog, entry],
      })),
    })),

  setCorrectedImage: (id, image, quad) =>
    set((state) => ({
      sessions: patchSession(state.sessions, id, (s) => ({
        ...s,
        correctedImage: image,
        ...(quad !== undefined ? { detectedQuad: quad } : {}),
      })),
    })),

  setAwaitingReview: (id) =>
    set((state) => ({
      sessions: patchSession(state.sessions, id, (s) => ({
        ...s,
        state: { kind: 'review' },
      })),
    })),

  setSessionLang: (id, lang) => {
    set((state) => ({
      sessions: patchSession(state.sessions, id, (s) => ({ ...s, lang })),
    }));
  },

  resetForReprocess: (id) => {
    set((state) => ({
      sessions: patchSession(state.sessions, id, (s) => ({
        ...s,
        state: { kind: 'queued' },
        receipt: null,
        ocrDebug: null,
        debugLog: [],
        correctedImage: null,
        findings: [],
      })),
    }));
    // Session is re-entering in-flight state; remove its persisted copy
    // so a refresh during reprocess doesn't restore stale receipt data.
    if (useAppStore.getState().persistEnabled) {
      void storageDeleteSession(id);
    }
  },

  reorderSessions: (fromIdx, toIdx) => {
    set((state) => {
      const len = state.sessions.length;
      if (fromIdx < 0 || fromIdx >= len) return {};
      if (toIdx < 0 || toIdx >= len) return {};
      if (fromIdx === toIdx) return {};
      const next = [...state.sessions];
      const [moved] = next.splice(fromIdx, 1);
      if (moved === undefined) return {};
      next.splice(toIdx, 0, moved);
      // Re-index positions to match the new array order so a later
      // hydrate() call reading from IDB rebuilds the same sequence.
      return {
        sessions: next.map((s, i) =>
          s.position === i ? s : { ...s, position: i },
        ),
      };
    });
    // Persist every terminal-state session whose position may have
    // shifted. Cheap for typical batches.
    persistAllTerminal();
  },

  removeSession: (id) => {
    set((state) => ({
      sessions: state.sessions.filter((s) => s.id !== id),
    }));
    if (useAppStore.getState().persistEnabled) {
      void storageDeleteSession(id);
    }
  },

  removeTerminal: () => {
    set((state) => ({
      sessions: state.sessions.filter(
        (s) => s.state.kind !== 'success' && s.state.kind !== 'error',
      ),
    }));
    // IDB only ever holds terminal-state sessions (see persistIfEnabled),
    // so removing all terminal in-memory rows is equivalent to clearing IDB.
    if (useAppStore.getState().persistEnabled) {
      void clearAllSessions();
    }
  },

  reset: () => {
    set({ sessions: [] });
    if (useAppStore.getState().persistEnabled) {
      void clearAllSessions();
    }
  },
}));

/**
 * Persist the current snapshot of a session to IndexedDB iff persistence
 * is enabled AND the session is in a terminal state. We deliberately
 * skip in-flight states (queued / processing) — they're transient, the
 * worker has no way to resume one across a page reload, and saving
 * them would just leave orphans on the next visit. Called from the
 * three actions that mark a state transition into terminal or change
 * the receipt afterwards (setResult, setError, updateReceipt).
 */
/**
 * Write every terminal-state session to IndexedDB, iff persistence is
 * enabled. In-flight sessions are skipped — same policy as
 * persistIfEnabled below; only success/error rows ever live in IDB.
 */
function persistAllTerminal(): void {
  const state = useAppStore.getState();
  if (!state.persistEnabled) return;
  for (const s of state.sessions) {
    if (s.state.kind === 'success' || s.state.kind === 'error') {
      void storageSaveSession(s);
    }
  }
}

function persistIfEnabled(id: string): void {
  const state = useAppStore.getState();
  if (!state.persistEnabled) return;
  const session = state.sessions.find((s) => s.id === id);
  if (session === undefined) return;
  if (session.state.kind !== 'success' && session.state.kind !== 'error') return;
  void storageSaveSession(session);
}

// Expose the store + a debug-log dumper on window when DEBUG is on, so the
// user can paste a one-liner into the DevTools console to grab a downloadable
// transcript of the latest session. Tree-shakes when DEBUG is false.
if (DEBUG && typeof window !== 'undefined') {
  const w = window as unknown as {
    __rrStore?: typeof useAppStore;
    __rrDumpDebug?: (sessionIndex?: number) => void;
  };
  w.__rrStore = useAppStore;
  w.__rrDumpDebug = (sessionIndex = 0): void => {
    const session = useAppStore.getState().sessions[sessionIndex];
    if (session === undefined) {
      console.warn(`[rr] no session at index ${sessionIndex}`);
      return;
    }
    const log = session.debugLog
      .map((e) => {
        const head = `${String(e.t).padStart(6)}ms [${e.stage}] ${e.label}`;
        return e.data === undefined
          ? head
          : `${head}\n${JSON.stringify(e.data, null, 2)}`;
      })
      .join('\n');
    const blob = new Blob([log], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `debug-${session.filename}.txt`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
}
